/**
 * Analytics for the masonry slider (see docs/MASONRY_ANALYTICS.md).
 *
 * Publishes interaction events to two buses:
 *   - Shopify.analytics.publish(...) — subscribe from Shopify Custom Pixels
 *   - window.dataLayer — consume from Google Tag Manager
 * Forward each event to a destination from exactly ONE of those places to
 * avoid double counting.
 *
 * Events:
 *   masonry_slide_view  — a masonry slide became visible (once per slide
 *                         per page load; covers initial view, arrows, dots,
 *                         swipe, and autoplay rotation)
 *   masonry_tile_click  — a linked tile was clicked
 *   masonry_video_play  — a tile video started via the playback button
 *   masonry_video_pause — a tile video paused via the playback button
 *
 * All listeners are document-level and this module runs once per page, so
 * section re-renders can't double-bind and each interaction fires exactly
 * one event.
 */

const seenSlides = new Set();

/**
 * @param {string} eventName
 * @param {Record<string, unknown>} payload
 */
function publish(eventName, payload) {
  const data = { source: 'masonry-slider', ...payload };

  try {
    window.Shopify?.analytics?.publish?.(eventName, data);
  } catch (publishError) {
    // Analytics must never break the storefront.
  }

  try {
    (window.dataLayer = window.dataLayer || []).push({ event: eventName, ...data });
  } catch (dataLayerError) {
    // Ignore.
  }
}

/**
 * @param {Element} slide
 * @returns {Record<string, unknown>}
 */
function slidePayload(slide) {
  const scroller = slide.closest('slideshow-slides');
  const slides = scroller ? Array.from(scroller.querySelectorAll('slideshow-slide')) : [];

  return {
    slide_id: slide.getAttribute('slide-id') || null,
    slide_index: slides.indexOf(slide),
    tile_count: slide.querySelectorAll('.masonry-tile').length,
  };
}

/**
 * @param {Element} slide
 */
function trackSlideView(slide) {
  const id = slide.getAttribute('slide-id') || 'masonry-slide';
  if (seenSlides.has(id)) return;
  seenSlides.add(id);

  publish('masonry_slide_view', slidePayload(slide));
}

const impressionObserver = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) trackSlideView(entry.target);
    }
  },
  { threshold: 0.5 }
);

function observeMasonrySlides() {
  for (const slide of document.querySelectorAll('slideshow-slide.masonry-slide')) {
    impressionObserver.observe(slide);
  }
}

observeMasonrySlides();
document.addEventListener('shopify:section:load', observeMasonrySlides);

// Slide navigation (arrows, dots, swipe, autoplay).
document.addEventListener('slideshow:select', (event) => {
  const slide = /** @type {{ detail?: { slide?: unknown } }} */ (event).detail?.slide;
  if (slide instanceof Element && slide.classList.contains('masonry-slide')) {
    trackSlideView(slide);
  }
});

document.addEventListener('click', (event) => {
  const target = event.target instanceof Element ? event.target : null;
  if (!target) return;

  const link = target.closest('.masonry-tile__link');
  if (link) {
    const tile = link.closest('.masonry-tile');
    const slide = link.closest('slideshow-slide');

    publish('masonry_tile_click', {
      tile_ref: link.getAttribute('data-mh-ref') || null,
      tile_label: link.getAttribute('aria-label') || null,
      tile_url: link.getAttribute('href') || null,
      media_type: tile?.querySelector('masonry-tile-video') ? 'video' : 'image',
      ...(slide ? slidePayload(slide) : {}),
    });
    return;
  }

  const playback = target.closest('.masonry-tile__playback');
  if (playback) {
    const component = playback.closest('masonry-tile-video');
    const video = component?.querySelector('video');
    const slide = playback.closest('slideshow-slide');
    if (!video) return;

    // The playback component toggles state on this same click; read the
    // settled state on the next tick so the event reflects what happened.
    setTimeout(() => {
      publish(video.paused ? 'masonry_video_pause' : 'masonry_video_play', {
        tile_label: playback.closest('.masonry-tile')?.querySelector('.masonry-tile__link')?.getAttribute('aria-label') || null,
        ...(slide ? slidePayload(slide) : {}),
      });
    }, 0);
  }
});
